/**
 * Voice transcription service.
 *
 * Primary:  Web Speech API (SpeechRecognition) — zero latency, works offline
 *           on mobile browsers that support it.
 * Fallback: Audio blob upload to a Whisper-compatible endpoint via the
 *           existing invokeFunction layer.
 */

import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'
import { safeLogger } from '../logger'

// ── Types ───────────────────────────────────────────────────────────────────

export interface TranscriptResult {
  text: string
  source: 'web_speech' | 'whisper' | 'manual'
  confidence: number
  duration_ms: number
}

export type RecognitionStatus = 'idle' | 'listening' | 'processing' | 'error'

export interface RecognitionCallbacks {
  onInterim?: (text: string) => void
  onFinal?: (result: TranscriptResult) => void
  onStatusChange?: (status: RecognitionStatus) => void
  onError?: (error: string) => void
}

// ── Web Speech API wrapper ──────────────────────────────────────────────────

const SpeechRecognition =
  typeof window !== 'undefined'
    ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : null

export function isWebSpeechAvailable(): boolean {
  return SpeechRecognition != null
}

export function createRecognitionSession(callbacks: RecognitionCallbacks): {
  start: () => void
  stop: () => void
  abort: () => void
} {
  if (!SpeechRecognition) {
    return {
      start: () => callbacks.onError?.('Web Speech API not available'),
      stop: () => {},
      abort: () => {},
    }
  }

  const recognition = new SpeechRecognition()
  recognition.continuous = true
  recognition.interimResults = true
  recognition.lang = 'en-US'
  recognition.maxAlternatives = 1

  let finalText = ''
  let startTime = 0

  recognition.onstart = () => {
    startTime = Date.now()
    finalText = ''
    callbacks.onStatusChange?.('listening')
  }

  recognition.onresult = (event: any) => {
    let interim = ''
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i]
      if (result.isFinal) {
        finalText += result[0].transcript + ' '
      } else {
        interim += result[0].transcript
      }
    }
    if (interim) callbacks.onInterim?.(finalText + interim)
  }

  recognition.onerror = (event: any) => {
    // 'no-speech' and 'aborted' are not real errors in survey context
    if (event.error === 'no-speech' || event.error === 'aborted') return
    callbacks.onStatusChange?.('error')
    callbacks.onError?.(event.error)
    safeLogger.logError('web_speech_error', crypto.randomUUID())
  }

  recognition.onend = () => {
    const duration = Date.now() - startTime
    const trimmed = finalText.trim()
    if (trimmed) {
      callbacks.onFinal?.({
        text: trimmed,
        source: 'web_speech',
        confidence: 0.85, // Web Speech doesn't expose aggregate confidence
        duration_ms: duration,
      })
    }
    callbacks.onStatusChange?.('idle')
  }

  return {
    start: () => {
      try {
        recognition.start()
      } catch {
        // Already started — ignore
      }
    },
    stop: () => {
      try {
        recognition.stop()
      } catch {
        // Already stopped
      }
    },
    abort: () => {
      try {
        recognition.abort()
      } catch {
        // Already aborted
      }
    },
  }
}

// ── Whisper fallback ────────────────────────────────────────────────────────

/**
 * Whisper edge function is not shipped (#1804). Default OFF so callers do not
 * hit a 404/503 forever. Enable only after `transcribe` is deployed and
 * OPENAI/Whisper secrets are configured: VITE_ENABLE_WHISPER_FALLBACK=true.
 */
export function isWhisperFallbackEnabled(): boolean {
  try {
    return String(import.meta.env.VITE_ENABLE_WHISPER_FALLBACK || '').toLowerCase() === 'true'
  } catch {
    return false
  }
}

export async function transcribeWithWhisper(audioBlob: Blob): Promise<TranscriptResult> {
  if (!isWhisperFallbackEnabled()) {
    throw toApiRequestError(
      {
        message: 'Whisper fallback is not configured (no transcribe edge function)',
        code: 'transcribe_not_configured',
        trace_id: 'local',
        status: 503,
      },
      'Whisper fallback is not configured',
    )
  }

  const startTime = Date.now()

  // Convert blob to base64 for the edge function
  const buffer = await audioBlob.arrayBuffer()
  const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)))

  const { data, error } = await invokeFunction<unknown>('transcribe', {
    method: 'POST',
    path: '/audio',
    body: {
      audio_base64: base64,
      content_type: audioBlob.type || 'audio/webm',
      language: 'en',
    },
  })

  if (error || !data) {
    throw toApiRequestError(error, 'Whisper transcription failed')
  }

  const result = data as Record<string, unknown>
  const text = typeof result.text === 'string' ? result.text.trim() : ''
  const confidence = typeof result.confidence === 'number' ? result.confidence : 0.9

  safeLogger.logEvent('whisper_transcribed', { status: 200 })

  return {
    text,
    source: 'whisper',
    confidence,
    duration_ms: Date.now() - startTime,
  }
}

// ── Audio recording via MediaRecorder ───────────────────────────────────────

export function createAudioRecorder(): {
  start: () => Promise<void>
  stop: () => Promise<Blob>
  isRecording: () => boolean
} {
  let mediaRecorder: MediaRecorder | null = null
  let chunks: Blob[] = []
  let stream: MediaStream | null = null

  return {
    start: async () => {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm'
      mediaRecorder = new MediaRecorder(stream, { mimeType })
      chunks = []
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data)
      }
      mediaRecorder.start(1000) // 1s chunks for progress indication
    },

    stop: () => {
      return new Promise<Blob>((resolve, reject) => {
        if (!mediaRecorder || mediaRecorder.state === 'inactive') {
          reject(new Error('Recorder not active'))
          return
        }
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: mediaRecorder?.mimeType || 'audio/webm' })
          // Release mic
          stream?.getTracks().forEach((t) => t.stop())
          stream = null
          resolve(blob)
        }
        mediaRecorder.stop()
      })
    },

    isRecording: () => mediaRecorder?.state === 'recording',
  }
}
